/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { useLazyGetByCpQuery } from "@api/postalCodes";
import { customStylesAsterisk } from "@components/customStylesInputs/inputsStyles";
import { InactivityHandler } from "@components/InactivityHandler";
import CustomRightImage from "@components/CustomRightImagev2";
import { buildProductRoute, formatCurrencyFromNumber, formatCurrencyInput, toNumber } from "@helpers/funciones";
import { PHONE_NUMBER } from "@utils/expreciones";
import useSolicitudCreditoForm from "@hooks/useSolicitudCreditoForm";
import {
  useLazyGetPersonEconomicEvaluationQuery,
  useLazyGetPersonEmploymentQuery,
  useLazyGetPersonsQuery,
  usePatchPersonEconomicEvaluationMutation,
  usePatchPersonEmploymentMutation,
  usePostPersonEconomicEvaluationMutation,
  usePostPersonEmploymentMutation,
} from "@api/personasApi";
import {
  Address,
  AddressData,
  ApiError,
  EconomicEvaluationStructure,
  EmploymentInfoStructure,
} from "@interfaces/index";
import { setModalErrorVisible } from "@store/slices/appSlice";
import * as Sentry from "@sentry/react";
import { useLazyGetAddressQuery, usePatchAddressMutation, usePostAddressMutation } from "@api/address";
import { useLazyGetAddressTypeQuery, useLazyGetPersonTypeQuery } from "@api/catalogs";
import { useLazyGetLoanApplicationByIdQuery } from "@api/applications";
import { ADDRESS_CODES, PERSON_CATALOG } from "@helpers/constantes";
import { UUID } from "crypto";

// TODO: falta un catálogo real para tipo de vivienda; mientras no exista, se usan estas
// opciones fijas en el front.
const TIPO_VIVIENDA_OPTIONS = [
  { value: "PROPIA", label: "Propia" },
  { value: "RENTADA", label: "Rentada" },
  { value: "FAMILIAR", label: "Familiar" },
  { value: "HIPOTECADA", label: "Hipotecada" },
];

const SolicitudCreditoPart4PF: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useDispatch();

  const { isVisible, isRequired, requiredField } = useSolicitudCreditoForm("step_4");

  const application = useSelector((state: RootState) => state.register.application);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);

  const [getPersonEconomicEvaluation] = useLazyGetPersonEconomicEvaluationQuery();
  const [getPersonEmploymentInfo] = useLazyGetPersonEmploymentQuery();
  const [getByCp] = useLazyGetByCpQuery();
  const [getAddress] = useLazyGetAddressQuery();
  const [getAddresType] = useLazyGetAddressTypeQuery();
  const [getApplicationById] = useLazyGetLoanApplicationByIdQuery();
  const [getPersons] = useLazyGetPersonsQuery();
  const [getPersonType] = useLazyGetPersonTypeQuery();

  const [postPersonEconomicEvaluation] = usePostPersonEconomicEvaluationMutation();
  const [patchPersonEconomicEvaluation] = usePatchPersonEconomicEvaluationMutation();
  const [postPersonEmployment] = usePostPersonEmploymentMutation();
  const [patchPersonEmployment] = usePatchPersonEmploymentMutation();
  const [postAddress] = usePostAddressMutation();
  const [patchAddress] = usePatchAddressMutation();

  const [colonias, setColonias] = useState<{ id: number; name: string }[]>([]);

  const [addresTypeLaboral, setAddresTypeLaboral] = useState<Address | undefined>(undefined);

  const [addressLaboral, setAddressLaboral] = useState<AddressData | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [idRepresentative, setIdRepresentative] = useState<string>("");
  // Si ya existe un registro (viene en el listado de economic_evaluation) se usa PATCH con este
  // id; si no existe todavía, se usa POST.
  const [economicEvaluationId, setEconomicEvaluationId] = useState<string>("");
  // Mismo criterio para employment: PATCH si ya hay un registro, POST si no.
  const [employmentId, setEmploymentId] = useState<string>("");

  const formik = useFormik({
    initialValues: {
      empresa_nombre: "",
      giro_laboral: "",
      puesto: "",

      // Domicilio laboral
      codigoPostal_laboral: "",
      calle_laboral: "",
      noExterior_laboral: "",
      noInterior_laboral: "",
      coloniaId_laboral: "",
      otro_laboral: "",
      estadoId_laboral: "",
      estado_laboral: "",
      ciudadId_laboral: "",
      ciudad_laboral: "",
      municipioId_laboral: "",
      municipio_laboral: "",

      telefono_laboral: "",
      antiguedad_empleo: "",
      fecha_ingreso: "",

      ingreso_principal: "",
      ingreso_conyuge: "",
      otros_ingresos: "",
      renta: "",
      gastos_fijos: "",
      gastos_familiares: "",
      pago_hipoteca: "",
      otros_gastos: "",
      num_dependientes_economicos: "",
      tipo_vivienda: "",
      anios_antiguedad_vivienda: "",
    },
    validationSchema: yup.object({
      empresa_nombre: requiredField(yup.string(), "empresa_nombre", true),
      giro_laboral: requiredField(yup.string(), "giro_laboral", true),
      puesto: requiredField(yup.string(), "puesto", true),

      codigoPostal_laboral: requiredField(
        yup.string().matches(/^[0-9]{5}$/, "Debe ser un código postal válido"),
        "domicilio_laboral",
        true,
        "Requerido",
      ),
      calle_laboral: requiredField(yup.string(), "domicilio_laboral", true, "Requerido"),
      noExterior_laboral: requiredField(yup.string(), "domicilio_laboral", true, "Requerido"),
      noInterior_laboral: requiredField(yup.string(), "domicilio_laboral", false),
      coloniaId_laboral: requiredField(yup.string(), "domicilio_laboral", true, "Requerido"),
      otro_laboral: yup.string().when("coloniaId_laboral", {
        is: "-1",
        then: (schema) => requiredField(schema, "domicilio_laboral", true),
        otherwise: (schema) => schema.notRequired(),
      }),

      telefono_laboral: requiredField(
        yup.string().matches(PHONE_NUMBER, { message: "Número de teléfono inválido", excludeEmptyString: true }),
        "telefono_laboral",
        true,
      ),
      antiguedad_empleo: requiredField(yup.string(), "antiguedad_empleo", true),
      fecha_ingreso: requiredField(yup.string(), "fecha_ingreso", true),

      ingreso_principal: requiredField(yup.string().matches(/^[0-9,]*$/, "Solo números"), "ingreso_principal", true),
    }),
    onSubmit: async (values) => {
      try {
        setIsLoading(true);

        const economicEvaluationBody: EconomicEvaluationStructure = {
          main_income: toNumber(values.ingreso_principal),
          spouse_income: toNumber(values.ingreso_conyuge),
          other_income: toNumber(values.otros_ingresos),
          rent: toNumber(values.renta),
          fixed_expenses: toNumber(values.gastos_fijos),
          family_expenses: toNumber(values.gastos_familiares),
          mortgage_payment: toNumber(values.pago_hipoteca),
          other_expenses: toNumber(values.otros_gastos),
          number_of_dependents: toNumber(values.num_dependientes_economicos),
          housing_type: values.tipo_vivienda,
          housing_seniority: toNumber(values.anios_antiguedad_vivienda),
        };

        if (economicEvaluationId) {
          await patchPersonEconomicEvaluation({
            personId: idRepresentative,
            idEconomic: economicEvaluationId,
            body: economicEvaluationBody,
          }).unwrap();
        } else {
          await postPersonEconomicEvaluation({
            personId: idRepresentative,
            body: economicEvaluationBody,
          }).unwrap();
        }

        const employmentBody: EmploymentInfoStructure = {
          company_name: values.empresa_nombre,
          business_activity: values.giro_laboral,
          position: values.puesto,
          phone_number: values.telefono_laboral,
          seniority: values.antiguedad_empleo,
          hire_date: values.fecha_ingreso,
        };

        if (employmentId) {
          await patchPersonEmployment({
            personId: idRepresentative,
            idEmployment: employmentId,
            body: employmentBody,
          }).unwrap();
        } else {
          await postPersonEmployment({
            personId: idRepresentative,
            body: employmentBody,
          }).unwrap();
        }

        if (isVisible("domicilio_laboral")) {
          const payload_address_laboral = {
            client_details_id: clientDetails.id,
            address_type_id: addresTypeLaboral?.id,
            person_id: idRepresentative,
            cp: values.codigoPostal_laboral,
            street: values.calle_laboral,
            no_ext: values.noExterior_laboral,
            no_int: values.noInterior_laboral,
            country_id: 1,
            state_id: values.estadoId_laboral ? +values.estadoId_laboral : null,
            city_id: values.ciudadId_laboral ? +values.ciudadId_laboral : null,
            colonia_id: Number(values.coloniaId_laboral) === -1 ? null : Number(values.coloniaId_laboral),
            other_neighborhood: values.otro_laboral,
            locality_id: values.municipioId_laboral ? +values.municipioId_laboral : null,
          };

          if (addressLaboral?.id) {
            await patchAddress({
              addressId: addressLaboral.id as UUID,
              data: payload_address_laboral,
            }).unwrap();
          } else {
            await postAddress(payload_address_laboral).unwrap();
          }
        }

        navigate(buildProductRoute(location.pathname, "expediente_pf"));
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "SolicitudCreditoPart4PF-submit",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    },
  });

  const totalIngresos =
    toNumber(formik.values.ingreso_principal) +
    toNumber(formik.values.ingreso_conyuge) +
    toNumber(formik.values.otros_ingresos);

  const totalGastos =
    toNumber(formik.values.renta) +
    toNumber(formik.values.gastos_fijos) +
    toNumber(formik.values.gastos_familiares) +
    toNumber(formik.values.pago_hipoteca) +
    toNumber(formik.values.otros_gastos);

  const fetchLocationDataLaboral = async (codigoPostal: string, coloniaId?: number | string) => {
    try {
      if (codigoPostal.length === 5) {
        const value = await getByCp(codigoPostal).unwrap();
        const { colonias, cities, states, localities } = value;
        setColonias(colonias);

        formik.setFieldValue("estadoId_laboral", states[0]?.id || "");
        formik.setFieldValue("estado_laboral", states[0]?.name || "");
        formik.setFieldValue("ciudadId_laboral", cities[0]?.id || "");
        formik.setFieldValue("ciudad_laboral", cities[0]?.name || "");
        formik.setFieldValue("municipioId_laboral", localities[0]?.id || "");
        formik.setFieldValue("municipio_laboral", localities[0]?.name || "");

        if (coloniaId) {
          const coloniaSelected = colonias.find((item: any) => String(item.id) === String(coloniaId));
          formik.setFieldValue("coloniaId_laboral", coloniaSelected?.id || "");
        } else {
          formik.setFieldValue("coloniaId_laboral", colonias[0]?.id || "");
        }

        setTimeout(() => {
          formik.validateForm();
        }, 200);
      } else {
        formik.setFieldValue("estadoId_laboral", "");
        formik.setFieldValue("estado_laboral", "");
        formik.setFieldValue("ciudadId_laboral", "");
        formik.setFieldValue("ciudad_laboral", "");
        formik.setFieldValue("municipioId_laboral", "");
        formik.setFieldValue("municipio_laboral", "");
        formik.setFieldValue("coloniaId_laboral", "");
      }
    } catch (error) {
      console.error("Error obteniendo datos del código postal (laboral):", error);
    }
  };

  const saveAddressLaboral = async (e: React.FocusEvent<HTMLInputElement>) => {
    try {
      formik.handleBlur(e);
      if (!addresTypeLaboral || !clientDetails?.id) return;

      const {
        codigoPostal_laboral: codigoPostal_err,
        calle_laboral: calle_err,
        noExterior_laboral: noExterior_err,
        noInterior_laboral: noInterior_err,
        coloniaId_laboral: coloniaId_err,
      } = formik.errors;

      if (codigoPostal_err || calle_err || noExterior_err || noInterior_err || coloniaId_err) {
        return;
      }

      const payload_address = {
        client_details_id: clientDetails.id,
        address_type_id: addresTypeLaboral.id,
        person_id: idRepresentative,
        cp: formik.values.codigoPostal_laboral,
        street: formik.values.calle_laboral,
        no_ext: formik.values.noExterior_laboral,
        no_int: formik.values.noInterior_laboral,
        country_id: 1,
        state_id: formik.values.estadoId_laboral ? +formik.values.estadoId_laboral : null,
        city_id: formik.values.ciudadId_laboral ? +formik.values.ciudadId_laboral : null,
        colonia_id: Number(formik.values.coloniaId_laboral) === -1 ? null : Number(formik.values.coloniaId_laboral),
        other_neighborhood: formik.values.otro_laboral,
      };

      if (addressLaboral?.id) {
        await patchAddress({
          addressId: addressLaboral.id as UUID,
          data: payload_address,
        }).unwrap();
      } else {
        const newAddress = await postAddress(payload_address).unwrap();
        setAddressLaboral(newAddress.data);
      }
    } catch (error) {
      console.error("Error guardando dirección laboral:", error);
    }
  };

  useEffect(() => {
    if (String(formik.values.coloniaId_laboral) !== "-1") {
      formik.setFieldValue("otro_laboral", "");
    }
  }, [formik.values.coloniaId_laboral]);

  const handleOnClickRestart = () => {
    navigate(buildProductRoute(location.pathname, "expediente_pf"));
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);

        const [loan_application, res_address_type, res_persons, res_person_type] = await Promise.all([
          getApplicationById(application.id).unwrap(),
          getAddresType().unwrap(),
          getPersons({
            loanApplicationId: application.id,
          }).unwrap(),
          getPersonType().unwrap(),
        ]);

        if (!loan_application) {
          throw new Error("No se encontró la solicitud de crédito");
        }

        const address_type_laboral = res_address_type.find((item: Address) => item.code === ADDRESS_CODES.laboral.code);

        const person_type_representative = res_person_type.find(
          (item) => item.code === PERSON_CATALOG.representante_legal.code,
        );

        const person_representative = res_persons.find((item) =>
          item.person_types_detail.some((detail) => detail.id === person_type_representative?.id),
        );

        const address_response_laboral = await getAddress({
          personId: String(person_representative?.id),
        }).unwrap();

        const addressLaboralPerson = address_response_laboral.find(
          (item: AddressData) => item.address_type_id === address_type_laboral?.id,
        );

        const economic_evaluation = await getPersonEconomicEvaluation(String(person_representative?.id)).unwrap();

        const employmentInfo = await getPersonEmploymentInfo(String(person_representative?.id)).unwrap();

        if (economic_evaluation) {
          formik.setFieldValue("ingreso_principal", formatCurrencyFromNumber(economic_evaluation.main_income));
          formik.setFieldValue("ingreso_conyuge", formatCurrencyFromNumber(economic_evaluation.spouse_income));
          formik.setFieldValue("otros_ingresos", formatCurrencyFromNumber(economic_evaluation.other_income));
          formik.setFieldValue("renta", formatCurrencyFromNumber(economic_evaluation.rent));
          formik.setFieldValue("gastos_fijos", formatCurrencyFromNumber(economic_evaluation.fixed_expenses));
          formik.setFieldValue("gastos_familiares", formatCurrencyFromNumber(economic_evaluation.family_expenses));
          formik.setFieldValue("pago_hipoteca", formatCurrencyFromNumber(economic_evaluation.mortgage_payment));
          formik.setFieldValue("otros_gastos", formatCurrencyFromNumber(economic_evaluation.other_expenses));
          formik.setFieldValue("num_dependientes_economicos", String(economic_evaluation.number_of_dependents ?? ""));
          formik.setFieldValue("tipo_vivienda", economic_evaluation.housing_type ?? "");
          formik.setFieldValue("anios_antiguedad_vivienda", String(economic_evaluation.housing_seniority ?? ""));
          setEconomicEvaluationId(economic_evaluation.id ?? "");
        }

        if (employmentInfo) {
          formik.setFieldValue("empresa_nombre", employmentInfo.company_name ?? "");
          formik.setFieldValue("giro_laboral", employmentInfo.business_activity ?? "");
          formik.setFieldValue("puesto", employmentInfo.position ?? "");
          formik.setFieldValue("telefono_laboral", employmentInfo.phone_number ?? "");
          formik.setFieldValue("antiguedad_empleo", employmentInfo.seniority ?? "");
          formik.setFieldValue("fecha_ingreso", String(employmentInfo.hire_date ?? "").slice(0, 10));
          setEmploymentId(employmentInfo.id ?? "");
        }

        if (addressLaboralPerson) {
          formik.setFieldValue("codigoPostal_laboral", addressLaboralPerson.cp || "");
          formik.setFieldValue("calle_laboral", addressLaboralPerson.street || "");
          formik.setFieldValue("noExterior_laboral", addressLaboralPerson.no_ext || "");
          formik.setFieldValue("noInterior_laboral", addressLaboralPerson.no_int || "");
          formik.setFieldValue("otro_laboral", addressLaboralPerson.other_neighborhood || "");
          fetchLocationDataLaboral(addressLaboralPerson.cp, addressLaboralPerson.colonia_id);
        }

        setAddresTypeLaboral(address_type_laboral);
        setAddressLaboral(addressLaboralPerson);
        setIdRepresentative(String(person_representative?.id || ""));
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "SolicitudCreditoPart1-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <Box ml={3} mr={3}>
          <Box maxWidth="sm" mx="auto">
            <Typography
              mt={4}
              mb={1}
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={600}
              color={theme?.palette?.primary?.dark}
              textAlign={"center"}
            >
              Solicitud de crédito
            </Typography>
            <Typography
              variant={isMobileOrTablet ? "body2" : "body1"}
              fontWeight={800}
              color={theme?.palette?.primary?.dark}
              textAlign={"center"}
            >
              4{" "}
              <Typography
                component="span"
                variant={isMobileOrTablet ? "body2" : "body1"}
                fontWeight={400}
                color={theme?.palette?.primary?.dark}
              >
                de 4
              </Typography>
            </Typography>

            <form onSubmit={formik.handleSubmit} noValidate>
              <Typography
                textAlign={"center"}
                variant={isMobileOrTablet ? "body2" : "body1"}
                color={theme?.palette?.primary?.dark}
                fontWeight={600}
                my={1}
              >
                Datos laborales del solicitante
              </Typography>

              {isVisible("empresa_nombre") && (
                <TextField
                  id="empresa_nombre"
                  name="empresa_nombre"
                  label="Nombre de la empresa"
                  margin="normal"
                  required={isRequired("empresa_nombre", true)}
                  fullWidth
                  autoComplete="off"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.empresa_nombre && Boolean(formik.errors.empresa_nombre)}
                  helperText={formik.touched.empresa_nombre && formik.errors.empresa_nombre}
                  value={formik.values.empresa_nombre}
                  disabled={isLoading || formik.isSubmitting}
                />
              )}

              <Grid container spacing={1}>
                {isVisible("giro_laboral") && (
                  <Grid item xs={6}>
                    <TextField
                      id="giro_laboral"
                      name="giro_laboral"
                      label="Actividad / Giro"
                      margin="normal"
                      required={isRequired("giro_laboral", true)}
                      fullWidth
                      autoComplete="off"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.giro_laboral && Boolean(formik.errors.giro_laboral)}
                      helperText={formik.touched.giro_laboral && formik.errors.giro_laboral}
                      value={formik.values.giro_laboral}
                      disabled={isLoading || formik.isSubmitting}
                    />
                  </Grid>
                )}

                {isVisible("puesto") && (
                  <Grid item xs={6}>
                    <TextField
                      id="puesto"
                      name="puesto"
                      label="Puesto / Ocupación"
                      margin="normal"
                      required={isRequired("puesto", true)}
                      fullWidth
                      autoComplete="off"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.puesto && Boolean(formik.errors.puesto)}
                      helperText={formik.touched.puesto && formik.errors.puesto}
                      value={formik.values.puesto}
                      disabled={isLoading || formik.isSubmitting}
                    />
                  </Grid>
                )}
              </Grid>

              {isVisible("domicilio_laboral") && (
                <>
                  <Typography
                    textAlign={"center"}
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    color={theme?.palette?.primary?.dark}
                    fontWeight={600}
                    my={1}
                  >
                    Domicilio laboral
                  </Typography>
                  <FormControl fullWidth>
                    <TextField
                      fullWidth
                      id="calle_laboral"
                      name="calle_laboral"
                      label="Calle o avenida"
                      required={isRequired("domicilio_laboral", true)}
                      value={formik.values.calle_laboral}
                      onChange={formik.handleChange}
                      onBlur={saveAddressLaboral}
                      error={formik.touched.calle_laboral && Boolean(formik.errors.calle_laboral)}
                      helperText={formik.touched.calle_laboral ? formik.errors.calle_laboral : ""}
                      margin="normal"
                      disabled={isLoading || formik.isSubmitting}
                      inputProps={{ maxLength: 100 }}
                    />
                  </FormControl>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        id="noExterior_laboral"
                        name="noExterior_laboral"
                        label="No. Exterior"
                        required={isRequired("domicilio_laboral", true)}
                        value={formik.values.noExterior_laboral}
                        onChange={formik.handleChange}
                        onBlur={saveAddressLaboral}
                        error={formik.touched.noExterior_laboral && Boolean(formik.errors.noExterior_laboral)}
                        helperText={formik.touched.noExterior_laboral ? formik.errors.noExterior_laboral : ""}
                        margin="normal"
                        disabled={isLoading || formik.isSubmitting}
                        inputProps={{ maxLength: 20 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        id="noInterior_laboral"
                        name="noInterior_laboral"
                        label="No. Interior"
                        required={isRequired("domicilio_laboral", false)}
                        value={formik.values.noInterior_laboral}
                        onChange={formik.handleChange}
                        onBlur={saveAddressLaboral}
                        error={formik.touched.noInterior_laboral && Boolean(formik.errors.noInterior_laboral)}
                        helperText={formik.touched.noInterior_laboral ? formik.errors.noInterior_laboral : ""}
                        margin="normal"
                        disabled={isLoading || formik.isSubmitting}
                        inputProps={{ maxLength: 20 }}
                      />
                    </Grid>
                  </Grid>

                  <FormControl
                    fullWidth
                    required={isRequired("domicilio_laboral", true)}
                    sx={{
                      ...customStylesAsterisk,
                      mt: 1,
                    }}
                    error={formik.touched.coloniaId_laboral && Boolean(formik.errors.coloniaId_laboral)}
                  >
                    <InputLabel id={"coloniaId_laboral"}>Colonia</InputLabel>
                    <Select
                      labelId="coloniaId_laboral"
                      name="coloniaId_laboral"
                      id="coloniaId_laboral"
                      fullWidth
                      required={isRequired("domicilio_laboral", true)}
                      label="Colonia"
                      value={formik.values.coloniaId_laboral}
                      onBlur={saveAddressLaboral}
                      onChange={formik.handleChange}
                      error={formik.touched.coloniaId_laboral && Boolean(formik.errors.coloniaId_laboral)}
                      sx={{ textAlign: "left" }}
                      disabled={isLoading || formik.isSubmitting}
                    >
                      <MenuItem value="">Selecciona</MenuItem>
                      {colonias.map((colonia) => (
                        <MenuItem key={colonia.id} value={colonia.id}>
                          {colonia.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.coloniaId_laboral && formik.errors.coloniaId_laboral && (
                      <FormHelperText>{formik.errors.coloniaId_laboral}</FormHelperText>
                    )}
                  </FormControl>

                  {String(formik.values.coloniaId_laboral) === "-1" && (
                    <TextField
                      id="otro_laboral"
                      name="otro_laboral"
                      label="Otra Colonia"
                      margin="normal"
                      required={isRequired("domicilio_laboral", true)}
                      fullWidth
                      autoComplete="off"
                      onBlur={saveAddressLaboral}
                      onChange={formik.handleChange}
                      error={formik.touched.otro_laboral && Boolean(formik.errors.otro_laboral)}
                      helperText={formik.touched.otro_laboral && formik.errors.otro_laboral}
                      value={formik.values.otro_laboral}
                    />
                  )}

                  <TextField
                    fullWidth
                    id="codigoPostal_laboral"
                    name="codigoPostal_laboral"
                    label="Código Postal"
                    required={isRequired("domicilio_laboral", true)}
                    value={formik.values.codigoPostal_laboral}
                    onChange={(e) => {
                      const cp = e.target.value.replace(/[^0-9]/g, "");
                      formik.setFieldValue("codigoPostal_laboral", cp);
                      if (cp.length === 5) {
                        fetchLocationDataLaboral(cp);
                      }
                    }}
                    onBlur={saveAddressLaboral}
                    error={formik.touched.codigoPostal_laboral && Boolean(formik.errors.codigoPostal_laboral)}
                    helperText={formik.touched.codigoPostal_laboral ? formik.errors.codigoPostal_laboral : ""}
                    margin="normal"
                    disabled={isLoading || formik.isSubmitting}
                    inputProps={{ maxLength: 5 }}
                  />

                  <FormControl fullWidth>
                    <TextField
                      fullWidth
                      id="municipio_laboral"
                      name="municipio_laboral"
                      label="Municipio"
                      value={formik.values.municipio_laboral}
                      margin="normal"
                      disabled={true}
                    />
                  </FormControl>

                  <FormControl fullWidth>
                    <TextField
                      fullWidth
                      id="ciudad_laboral"
                      name="ciudad_laboral"
                      label="Ciudad"
                      value={formik.values.ciudad_laboral}
                      margin="normal"
                      disabled={true}
                    />
                  </FormControl>

                  <FormControl fullWidth>
                    <TextField
                      fullWidth
                      id="estado_laboral"
                      name="estado_laboral"
                      label="Entidad federativa"
                      value={formik.values.estado_laboral}
                      margin="normal"
                      disabled={true}
                    />
                  </FormControl>
                </>
              )}

              <Grid container spacing={1}>
                {isVisible("telefono_laboral") && (
                  <Grid item xs={6}>
                    <TextField
                      type="text"
                      name="telefono_laboral"
                      label="Teléfono"
                      margin="normal"
                      fullWidth
                      required={isRequired("telefono_laboral", true)}
                      value={formik.values.telefono_laboral}
                      onChange={(event) => {
                        formik.setFieldValue("telefono_laboral", event.target.value.replace(/[^0-9]/g, ""));
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.touched.telefono_laboral && Boolean(formik.errors.telefono_laboral)}
                      helperText={formik.touched.telefono_laboral && formik.errors.telefono_laboral}
                      inputProps={{ maxLength: 10, inputMode: "numeric" }}
                      disabled={isLoading || formik.isSubmitting}
                    />
                  </Grid>
                )}

                {isVisible("antiguedad_empleo") && (
                  <Grid item xs={6}>
                    <TextField
                      name="antiguedad_empleo"
                      label="Antigüedad en el empleo"
                      margin="normal"
                      fullWidth
                      required={isRequired("antiguedad_empleo", true)}
                      value={formik.values.antiguedad_empleo}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.antiguedad_empleo && Boolean(formik.errors.antiguedad_empleo)}
                      helperText={formik.touched.antiguedad_empleo && formik.errors.antiguedad_empleo}
                      disabled={isLoading || formik.isSubmitting}
                    />
                  </Grid>
                )}
              </Grid>

              {isVisible("fecha_ingreso") && (
                <TextField
                  type="date"
                  name="fecha_ingreso"
                  label="Fecha de ingreso"
                  margin="normal"
                  fullWidth
                  required={isRequired("fecha_ingreso", true)}
                  value={formik.values.fecha_ingreso}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.fecha_ingreso && Boolean(formik.errors.fecha_ingreso)}
                  helperText={formik.touched.fecha_ingreso && formik.errors.fecha_ingreso}
                  InputLabelProps={{ shrink: true }}
                  disabled={isLoading || formik.isSubmitting}
                />
              )}

              <Typography
                textAlign={"center"}
                variant={isMobileOrTablet ? "body2" : "body1"}
                color={theme?.palette?.primary?.dark}
                fontWeight={600}
                my={3}
              >
                Evaluación económica
              </Typography>

              <Grid container spacing={1}>
                {isVisible("ingreso_principal") && (
                  <Grid item xs={6}>
                    <TextField
                      name="ingreso_principal"
                      label="Ingreso principal"
                      margin="normal"
                      fullWidth
                      required={isRequired("ingreso_principal", true)}
                      value={formik.values.ingreso_principal}
                      onChange={(e) => formik.setFieldValue("ingreso_principal", formatCurrencyInput(e.target.value))}
                      onBlur={formik.handleBlur}
                      error={formik.touched.ingreso_principal && Boolean(formik.errors.ingreso_principal)}
                      helperText={formik.touched.ingreso_principal && formik.errors.ingreso_principal}
                      disabled={isLoading || formik.isSubmitting}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    />
                  </Grid>
                )}

                {isVisible("ingreso_conyuge") && (
                  <Grid item xs={6}>
                    <TextField
                      name="ingreso_conyuge"
                      label="Ingreso cónyuge"
                      margin="normal"
                      fullWidth
                      value={formik.values.ingreso_conyuge}
                      onChange={(e) => formik.setFieldValue("ingreso_conyuge", formatCurrencyInput(e.target.value))}
                      onBlur={formik.handleBlur}
                      disabled={isLoading || formik.isSubmitting}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    />
                  </Grid>
                )}
              </Grid>

              {isVisible("otros_ingresos") && (
                <TextField
                  name="otros_ingresos"
                  label="Otros ingresos"
                  margin="normal"
                  fullWidth
                  value={formik.values.otros_ingresos}
                  onChange={(e) => formik.setFieldValue("otros_ingresos", formatCurrencyInput(e.target.value))}
                  onBlur={formik.handleBlur}
                  disabled={isLoading || formik.isSubmitting}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
              )}

              <TextField
                label="Total ingresos"
                margin="normal"
                fullWidth
                value={totalIngresos.toLocaleString("en-US")}
                disabled
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />

              <Grid container spacing={1}>
                {isVisible("renta") && (
                  <Grid item xs={6}>
                    <TextField
                      name="renta"
                      label="Renta"
                      margin="normal"
                      fullWidth
                      value={formik.values.renta}
                      onChange={(e) => formik.setFieldValue("renta", formatCurrencyInput(e.target.value))}
                      onBlur={formik.handleBlur}
                      disabled={isLoading || formik.isSubmitting}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    />
                  </Grid>
                )}

                {isVisible("gastos_fijos") && (
                  <Grid item xs={6}>
                    <TextField
                      name="gastos_fijos"
                      label="Gastos fijos"
                      margin="normal"
                      fullWidth
                      value={formik.values.gastos_fijos}
                      onChange={(e) => formik.setFieldValue("gastos_fijos", formatCurrencyInput(e.target.value))}
                      onBlur={formik.handleBlur}
                      disabled={isLoading || formik.isSubmitting}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    />
                  </Grid>
                )}
              </Grid>

              <Grid container spacing={1}>
                {isVisible("gastos_familiares") && (
                  <Grid item xs={6}>
                    <TextField
                      name="gastos_familiares"
                      label="Gastos familiares"
                      margin="normal"
                      fullWidth
                      value={formik.values.gastos_familiares}
                      onChange={(e) => formik.setFieldValue("gastos_familiares", formatCurrencyInput(e.target.value))}
                      onBlur={formik.handleBlur}
                      disabled={isLoading || formik.isSubmitting}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    />
                  </Grid>
                )}

                {isVisible("pago_hipoteca") && (
                  <Grid item xs={6}>
                    <TextField
                      name="pago_hipoteca"
                      label="Pago hipoteca"
                      margin="normal"
                      fullWidth
                      value={formik.values.pago_hipoteca}
                      onChange={(e) => formik.setFieldValue("pago_hipoteca", formatCurrencyInput(e.target.value))}
                      onBlur={formik.handleBlur}
                      disabled={isLoading || formik.isSubmitting}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    />
                  </Grid>
                )}
              </Grid>

              {isVisible("otros_gastos") && (
                <TextField
                  name="otros_gastos"
                  label="Otros gastos"
                  margin="normal"
                  fullWidth
                  value={formik.values.otros_gastos}
                  onChange={(e) => formik.setFieldValue("otros_gastos", formatCurrencyInput(e.target.value))}
                  onBlur={formik.handleBlur}
                  disabled={isLoading || formik.isSubmitting}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
              )}

              <TextField
                label="Gastos totales"
                margin="normal"
                fullWidth
                value={totalGastos.toLocaleString("en-US")}
                disabled
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />

              {isVisible("num_dependientes_economicos") && (
                <TextField
                  name="num_dependientes_economicos"
                  label="No. dependientes económicos"
                  margin="normal"
                  fullWidth
                  value={formik.values.num_dependientes_economicos}
                  onChange={(e) =>
                    formik.setFieldValue("num_dependientes_economicos", e.target.value.replace(/[^0-9]/g, ""))
                  }
                  onBlur={formik.handleBlur}
                  disabled={isLoading || formik.isSubmitting}
                />
              )}

              <Grid container spacing={1}>
                {isVisible("tipo_vivienda") && (
                  <Grid item xs={6}>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                      <InputLabel id="tipo_vivienda">Tipo de vivienda</InputLabel>
                      <Select
                        labelId="tipo_vivienda"
                        id="tipo_vivienda"
                        name="tipo_vivienda"
                        fullWidth
                        label="Tipo de vivienda"
                        value={formik.values.tipo_vivienda}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        disabled={isLoading || formik.isSubmitting}
                      >
                        <MenuItem value="">Selecciona</MenuItem>
                        {TIPO_VIVIENDA_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {isVisible("anios_antiguedad_vivienda") && (
                  <Grid item xs={6}>
                    <TextField
                      name="anios_antiguedad_vivienda"
                      label="Años de antigüedad"
                      margin="normal"
                      fullWidth
                      value={formik.values.anios_antiguedad_vivienda}
                      onChange={(e) =>
                        formik.setFieldValue("anios_antiguedad_vivienda", e.target.value.replace(/[^0-9]/g, ""))
                      }
                      onBlur={formik.handleBlur}
                      disabled={isLoading || formik.isSubmitting}
                    />
                  </Grid>
                )}
              </Grid>

              <Grid container columnSpacing={1} my={4}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="blue_outlined"
                    sx={{ padding: "0", height: "100%" }}
                    onClick={handleOnClickRestart}
                    disabled={formik.isSubmitting}
                  >
                    Regresar al inicio
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    type="submit"
                    variant="default"
                    fullWidth
                    color="primary"
                    disabled={!formik.isValid || formik.isSubmitting}
                  >
                    Continuar
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Box>
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default SolicitudCreditoPart4PF;
